/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useCallback, useMemo, useEffect } from "react";
import { CartItemWithUUID, CrossSellTabType } from "@swiggy-private/cart-types";
import { isItemCustomizable } from "@cart-ui/helpers/Item";
import { separateCardsOnImageId } from "../utils";
import { ITabBarItem } from "@swiggy-private/dls-web-core/dist/components/TabContainer/components/TabBar/types";
import { PriceInfo } from "../types";

interface UseCrossSellCarouselProps {
    defaultTabSelected: CrossSellTabType | null;
    crossSellM2ItemsList?: Array<{
        cross_sell_suggestions?: CartItemWithUUID[];
        type?: CrossSellTabType;
        display_name?: string;
    }>;
    onItemCustomisation: (
        item: CartItemWithUUID,
        selectedTab?: CrossSellTabType | null,
        position?: string,
    ) => Promise<void>;
    addItem: (item: CartItemWithUUID, selectedTab?: CrossSellTabType | null, position?: string) => Promise<void>;
    onClickItemDetailsCarousel: (
        itemId: string,
        position: string,
        selectedTab?: CrossSellTabType | null,
        priceInfo?: PriceInfo,
    ) => void;
    onClickChangeTabCarousel: (selectedTab: CrossSellTabType | null) => void;
    onImpressionCarouselItemDetailsPopup?: (
        itemId: string,
        index: string,
        selectedTab?: CrossSellTabType | null,
    ) => void;
}

export const useCrossSellCarousel = ({
    defaultTabSelected,
    crossSellM2ItemsList,
    onItemCustomisation,
    addItem,
    onClickItemDetailsCarousel,
    onClickChangeTabCarousel,
    onImpressionCarouselItemDetailsPopup,
}: UseCrossSellCarouselProps) => {
    // Initialize with defaultTabSelected, but we'll validate it in useEffect
    const [selectedTab, setSelectedTab] = useState(defaultTabSelected);

    // Ensure selectedTab is valid and exists in crossSellM2ItemsList
    useEffect(() => {
        // If we have items in the list
        if (crossSellM2ItemsList?.length) {
            // Special case: If there's only one item in the list, select it regardless of its type
            if (crossSellM2ItemsList.length === 1) {
                // Even if the type is null/undefined, we want to select this item
                // This ensures we display the only available tab
                setSelectedTab(crossSellM2ItemsList[0].type || null);
                return;
            }

            // For multiple items, check if the current selectedTab exists in the list
            const tabExists = crossSellM2ItemsList.some((item) => item.type === selectedTab);

            // If not, default to the first item's type (even if it's null/undefined)
            if (!tabExists) {
                setSelectedTab(crossSellM2ItemsList[0]?.type || null);
            }
        }
        // No need for an else case - if the list is empty, we keep the current selectedTab
    }, [crossSellM2ItemsList, selectedTab]);

    const currentTabItems = useMemo(() => {
        return crossSellM2ItemsList?.find((item) => item.type === selectedTab) || crossSellM2ItemsList?.[0];
    }, [crossSellM2ItemsList, selectedTab]);

    const handleAddButtonClick = useCallback(
        (item: CartItemWithUUID, position?: string) => () => {
            if (isItemCustomizable(item)) {
                onItemCustomisation(item, selectedTab, position);
                return;
            }

            addItem(item, selectedTab, position);
        },
        [addItem, onItemCustomisation, selectedTab],
    );

    const handleItemDetailsClick = useCallback(
        (itemId: string, index: string, priceInfo?: PriceInfo) => () => {
            onClickItemDetailsCarousel(itemId, index, selectedTab, priceInfo);
        },
        [onClickItemDetailsCarousel, selectedTab],
    );

    const handleTabChange = useCallback(
        (tabItem) => {
            setSelectedTab(tabItem.id as unknown as CrossSellTabType);
            onClickChangeTabCarousel(tabItem.id as unknown as CrossSellTabType);
        },
        [onClickChangeTabCarousel],
    );

    const handleImpressionCarouselItemDetailsPopup = useCallback(
        (itemId: string, index: string) => {
            onImpressionCarouselItemDetailsPopup?.(itemId, index, selectedTab);
        },
        [onImpressionCarouselItemDetailsPopup, selectedTab],
    );

    const tabOptions = useMemo(() => {
        return (crossSellM2ItemsList?.map((item) => ({
            id: item.type,
            label: item.display_name,
            isSelected: item.type === selectedTab,
            meta: {},
        })) || []) as ITabBarItem[];
    }, [crossSellM2ItemsList, selectedTab]);

    const sortedItems = useMemo(
        () => separateCardsOnImageId(currentTabItems?.cross_sell_suggestions || []),
        [currentTabItems],
    );

    return {
        selectedTab,
        currentTabItems,
        sortedItems,
        tabOptions,
        handleAddButtonClick,
        handleItemDetailsClick,
        handleTabChange,
        handleImpressionCarouselItemDetailsPopup,
    };
};
